import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { createAdminNotification } from "./notificationsService";
import { ChevronLeft, ChevronRight, Check, Lock, RefreshCcw, Ticket, X } from "lucide-react";
import {
  getVenueSeatConfig,
  resolveEventSectionLayout,
} from "../lib/eventSeatLayout";
import { columnHeadersForPage, resolveSeatNumber } from "../lib/venueLayout";
import {
  getHoldableSelections,
  getOccupancyStatus as resolveOccupancyStatus,
  isActiveSeatHold,
  normalizeSeatLabel,
} from "../lib/seatHoldStatus";
import { resolveCheckoutSeatArea } from "../lib/seatSectionDisplay";
import { notifyBuyerOrderConfirmed } from "../lib/supportEmailNotify";
import { formatEventWhen } from "../lib/eventCancellationMail";
import { resolveEventPolicyHtml, venuePolicyToPlainText } from "../lib/venuePolicyHtml";

const mapIssuedTicketsForEmail = (rows, sourcePayload, tierList, attendeeName, attendeeEmail) =>
  (rows || []).map((row, index) => {
    const source = sourcePayload[index] || {};
    const tier = (tierList || []).find((item) => String(item.id) === String(row.ticket_tier_id || source.ticket_tier_id));
    return {
      qr_code: row.qr_code || source.qr_code || "",
      ticket_id: row.id,
      attendee_name: attendeeName || "Guest",
      attendee_email: attendeeEmail || "",
      tier_name: tier?.name || "",
      section: row.seat_area || source.seat_area || tier?.section || "",
      seat_label: row.seat_label || source.seat_label || "",
      price: Number(tier?.price || 0),
    };
  });

const SeatManagementModal = ({ event, onClose }) => {
  const [tiers, setTiers] = useState([]);
  const [soldSeatsByTier, setSoldSeatsByTier] = useState({});
  const [heldSeatsByTier, setHeldSeatsByTier] = useState({});
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [selectedSeatsByTier, setSelectedSeatsByTier] = useState({});
  const [pageIndexByTier, setPageIndexByTier] = useState({});
  const [unmappedSoldCount, setUnmappedSoldCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [message, setMessage] = useState("");
  const [confirmHoldOpen, setConfirmHoldOpen] = useState(false);
  const [issueTicketOpen, setIssueTicketOpen] = useState(false);
  const [issueAttendeeName, setIssueAttendeeName] = useState("");
  const [issueBuyerEmail, setIssueBuyerEmail] = useState("");
  const [issueFormError, setIssueFormError] = useState("");

  const currentVenueConfig = getVenueSeatConfig(event?.venue);

  const currentTier = useMemo(
    () => tiers.find((tier) => tier.id === selectedTierId) || tiers[0] || null,
    [selectedTierId, tiers]
  );

  const resolvedSeatLayout = useMemo(
    () => resolveEventSectionLayout({ event, tier: currentTier }),
    [currentTier, event]
  );
  const currentSectionLayout = resolvedSeatLayout.currentSectionLayout;

  const currentPageIndex = pageIndexByTier[currentTier?.id] || 0;
  const currentPage = currentSectionLayout?.pages ? currentSectionLayout.pages[currentPageIndex] : null;
  const currentPageSeatArea = String(currentPage?.label || currentSectionLayout?.label || "").trim();

  const fallbackPageLabelForTier = (tier) => {
    const layout = resolveEventSectionLayout({ event, tier }).currentSectionLayout;
    const pages = layout?.pages || [];
    if (pages.length === 1) return String(pages[0]?.label || "").trim();
    if (tier?.id === currentTier?.id) return currentPageSeatArea;
    return String(layout?.label || "").trim();
  };

  const resolveIssuedSeatArea = (tierId, seatLabel, seatArea = "") => {
    const tier = tiers.find((row) => row.id === tierId);
    return resolveCheckoutSeatArea({
      section: tier?.section || tier?.name || "",
      seatArea,
      seatLabel,
      fallbackPageLabel: fallbackPageLabelForTier(tier),
    });
  };

  const seatRows = currentPage?.rows ?? currentSectionLayout?.rows ?? currentVenueConfig.rows;
  const seatCols = currentPage
    ? columnHeadersForPage(currentPage)
    : currentSectionLayout?.rowSeatCounts
      ? Array.from(
          {
            length: Math.max(0, ...Object.values(currentSectionLayout.rowSeatCounts)),
          },
          (_, index) => index + 1
        )
      : currentVenueConfig.cols;
  const rowSeatCounts = currentPage ? null : currentSectionLayout?.rowSeatCounts || null;
  const seatLayout = currentPage?.layout ?? null;

  const activeSeatSet = useMemo(() => {
    const next = new Set();

    Object.entries(soldSeatsByTier).forEach(([tierId, seats]) => {
      (seats || []).forEach((seatLabel) => {
        next.add(`${tierId}::${normalizeSeatLabel(seatLabel)}`);
      });
    });

    Object.entries(heldSeatsByTier).forEach(([tierId, seats]) => {
      (seats || []).forEach((seatLabel) => {
        next.add(`${tierId}::${normalizeSeatLabel(seatLabel)}`);
      });
    });

    return next;
  }, [heldSeatsByTier, soldSeatsByTier]);

  const fetchSeatData = useCallback(async () => {
    if (!event?.id) return;

    setLoading(true);

    const { data: tiersData, error: tiersError } = await supabase
      .from("ticket_tiers")
      .select("id, event_id, name, price, section, quantity_total, seat_areas")
      .eq("event_id", event.id)
      .order("id", { ascending: true });

    const nextTiers = tiersData || [];
    const tierIds = nextTiers.map((tier) => tier.id).filter(Boolean);

    const ticketsQuery = tierIds.length
      ? supabase.from("tickets").select("ticket_tier_id, seat_label").in("ticket_tier_id", tierIds)
      : Promise.resolve({ data: [], error: null });

    const [{ data: ticketsData, error: ticketsError }, { data: holdsData, error: holdsError }] =
      await Promise.all([
        ticketsQuery,
        supabase
          .from("seat_holds")
          .select("ticket_tier_id, seat_label, hold_status, expires_at")
          .eq("event_id", event.id),
      ]);

    if (tiersError || ticketsError || holdsError) {
      console.error("Failed to load seat management data:", tiersError || ticketsError || holdsError);
    }

    setTiers(nextTiers);
    const soldNext = {};
    const heldNext = {};
    let unmappedCount = 0;
    tierIds.forEach((tierId) => {
      soldNext[tierId] = [];
      heldNext[tierId] = [];
    });

    (ticketsData || []).forEach((ticket) => {
      const tierId = ticket.ticket_tier_id;
      if (!tierId || !tierIds.includes(tierId)) return;
      const seatLabel = normalizeSeatLabel(ticket.seat_label);
      if (!seatLabel) {
        unmappedCount += 1;
        return;
      }
      soldNext[tierId].push(seatLabel);
    });

    (holdsData || []).forEach((hold) => {
      const tierId = hold.ticket_tier_id;
      if (!tierId || !tierIds.includes(tierId)) return;

      if (!isActiveSeatHold(hold)) return;

      const seatLabel = normalizeSeatLabel(hold.seat_label);
      if (!seatLabel) return;
      heldNext[tierId].push(seatLabel);
    });

    setSoldSeatsByTier(soldNext);
    setHeldSeatsByTier(heldNext);
    setUnmappedSoldCount(unmappedCount);
    setSelectedTierId((prev) => prev || nextTiers[0]?.id || null);
    setPageIndexByTier((prev) => {
      const next = { ...prev };
      nextTiers.forEach((tier) => {
        if (next[tier.id] == null) {
          next[tier.id] = 0;
        }
      });
      return next;
    });

    setLoading(false);
  }, [event?.id]);

  useEffect(() => {
    fetchSeatData();

    if (!event?.id) return undefined;

    const channel = supabase
      .channel(`seat-management-${event.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets" }, fetchSeatData)
      .on("postgres_changes", { event: "*", schema: "public", table: "seat_holds" }, fetchSeatData)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [event?.id, fetchSeatData]);

  const getOccupancyStatus = (tierId, seatId) =>
    resolveOccupancyStatus({
      soldSeats: soldSeatsByTier[tierId] || [],
      heldSeats: heldSeatsByTier[tierId] || [],
      blockedSet: activeSeatSet,
      tierId,
      seatId,
    });

  const getSeatStatus = (tierId, seatId) => {
    const normalizedSeatId = normalizeSeatLabel(seatId);

    if ((selectedSeatsByTier[tierId] || []).some((seat) => normalizeSeatLabel(seat.label) === normalizedSeatId)) {
      return "selected";
    }

    return getOccupancyStatus(tierId, normalizedSeatId);
  };

  const toggleSeatSelection = (tierId, seatId, seatArea = "") => {
    const status = getSeatStatus(tierId, seatId);
    if (status === "sold") return;

    setSelectedSeatsByTier((prev) => {
      const current = prev[tierId] || [];
      const isSelected = current.some((seat) => seat.id === seatId);
      const next = isSelected
        ? current.filter((seat) => seat.id !== seatId)
        : [...current, { id: seatId, label: seatId, seatArea: String(seatArea || "").trim() }];

      if (next.length === 0) {
        const { [tierId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [tierId]: next,
      };
    });
  };

  const clearSelection = () => {
    setSelectedSeatsByTier({});
  };

  const getSelectedSeatEntries = () => {
    const entries = [];

    Object.entries(selectedSeatsByTier).forEach(([tierId, seats]) => {
      (seats || []).forEach((seat) => {
        entries.push({
          tierId,
          seatLabel: normalizeSeatLabel(seat.label),
          seatArea: String(seat.seatArea || "").trim(),
        });
      });
    });

    return entries;
  };

  const refreshAfterAction = async (text) => {
    clearSelection();
    setMessage(text);
    await fetchSeatData();
    await createAdminNotification({
      title: "Seat management update",
      message: `${event?.title || "Event"}: ${text}`,
      meta: "Seats",
    });
  };

  const createTicketQr = () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const requestHold = () => {
    const selections = getHoldableSelections(getSelectedSeatEntries(), getOccupancyStatus);
    if (selections.length === 0) {
      setMessage("Select available seats to hold.");
      return;
    }
    setConfirmHoldOpen(true);
  };

  const performHold = async () => {
    const selections = getHoldableSelections(getSelectedSeatEntries(), getOccupancyStatus);

    if (selections.length === 0) {
      setConfirmHoldOpen(false);
      setMessage("Select available seats to hold.");
      return;
    }

    setConfirmHoldOpen(false);
    setBusyAction("hold");

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const payload = selections.map(({ tierId, seatLabel, seatArea }) => ({
      event_id: event.id,
      ticket_tier_id: tierId,
      seat_label: seatLabel,
      seat_area: resolveIssuedSeatArea(tierId, seatLabel, seatArea) || null,
      hold_status: "held",
      reserved_by: user?.id || null,
      note: "Admin hold",
    }));

    let { error } = await supabase.from("seat_holds").insert(payload);
    const missingHoldArea =
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("seat_area"));
    if (missingHoldArea) {
      const fallbackPayload = payload.map(({ seat_area, ...row }) => row);
      const fallbackResult = await supabase.from("seat_holds").insert(fallbackPayload);
      error = fallbackResult.error;
    }

    if (error) {
      console.error("Failed to hold seats:", error);
      setMessage(`Failed to hold seats: ${error.message}`);
      setBusyAction("");
      return;
    }

    await refreshAfterAction(`${selections.length} seat${selections.length === 1 ? "" : "s"} held.`);
    setBusyAction("");
  };

  const performRelease = async () => {
    const selections = getSelectedSeatEntries().filter(({ tierId, seatLabel }) => (heldSeatsByTier[tierId] || []).includes(seatLabel));

    if (selections.length === 0) {
      setMessage("Select held seats to release.");
      return;
    }

    setBusyAction("release");

    for (const selection of selections) {
      const { error } = await supabase
        .from("seat_holds")
        .update({ hold_status: "released", updated_at: new Date().toISOString() })
        .eq("event_id", event.id)
        .eq("ticket_tier_id", selection.tierId)
        .eq("seat_label", selection.seatLabel)
        .neq("hold_status", "released");

      if (error) {
        console.error("Failed to release seat:", error);
        setMessage(`Failed to release some seats: ${error.message}`);
        setBusyAction("");
        return;
      }
    }

    await refreshAfterAction(`${selections.length} held seat${selections.length === 1 ? "" : "s"} released.`);
    setBusyAction("");
  };

  const requestIssueTicket = () => {
    const selections = getSelectedSeatEntries().filter(({ tierId, seatLabel }) => (heldSeatsByTier[tierId] || []).includes(seatLabel));
    if (selections.length === 0) {
      setMessage("Select held seats to issue as tickets.");
      return;
    }
    setIssueFormError("");
    setIssueAttendeeName("");
    setIssueBuyerEmail("");
    setIssueTicketOpen(true);
  };

  const performIssueTicket = async () => {
    const selections = getSelectedSeatEntries().filter(({ tierId, seatLabel }) => (heldSeatsByTier[tierId] || []).includes(seatLabel));
    if (selections.length === 0) {
      setIssueTicketOpen(false);
      setMessage("Select held seats to issue as tickets.");
      return;
    }

    const attendeeName = String(issueAttendeeName || "").trim();
    const buyerEmail = String(issueBuyerEmail || "").trim();
    const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail);

    if (!attendeeName) {
      setIssueFormError("Enter the customer or attendee name.");
      return;
    }
    if (!buyerEmail) {
      setIssueFormError("Enter the buyer’s Echo Jham email.");
      return;
    }
    if (!emailLooksValid) {
      setIssueFormError("Enter a valid email address.");
      return;
    }

    setBusyAction("issue");
    setIssueFormError("");

    const { data: matchedUsers, error: userError } = await supabase
      .from("users")
      .select("id, email, full_name")
      .ilike("email", buyerEmail)
      .limit(1);

    if (userError) {
      console.error("Failed to look up buyer:", userError);
      setIssueFormError(`Unable to look up that account: ${userError.message}`);
      setBusyAction("");
      return;
    }

    const buyer = (matchedUsers || [])[0];
    if (!buyer?.id) {
      setIssueFormError("No Echo Jham account uses that email. The buyer must sign up first.");
      setBusyAction("");
      return;
    }

    const payload = selections.map(({ tierId, seatLabel, seatArea }) => ({
      ticket_tier_id: tierId,
      buyer_id: buyer.id,
      attendee_name: attendeeName,
      qr_code: createTicketQr(),
      seat_label: seatLabel,
      seat_area: resolveIssuedSeatArea(tierId, seatLabel, seatArea) || null,
      ticket_kind: "direct",
      source: "admin",
    }));

    let { data, error } = await supabase.from("tickets").insert(payload).select("id, qr_code, ticket_tier_id, seat_label, seat_area");

    const missingColumnError =
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("ticket_kind") ||
        String(error.message || "").toLowerCase().includes("attendee_name") ||
        String(error.message || "").toLowerCase().includes("source"));

    if (missingColumnError) {
      const fallbackPayload = payload.map(({ ticket_tier_id, buyer_id, qr_code, seat_label, seat_area }) => ({
        ticket_tier_id,
        buyer_id,
        qr_code,
        seat_label,
        seat_area,
      }));
      const fallbackResult = await supabase.from("tickets").insert(fallbackPayload).select("id, qr_code, ticket_tier_id, seat_label, seat_area");
      data = fallbackResult.data;
      error = fallbackResult.error;
    }

    if (
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("seat_area"))
    ) {
      const withoutArea = (payload || []).map(({ seat_area, ticket_kind, source, attendee_name, ...row }) => ({
        ticket_tier_id: row.ticket_tier_id,
        buyer_id: row.buyer_id,
        qr_code: row.qr_code,
        seat_label: row.seat_label,
      }));
      const areaFallback = await supabase.from("tickets").insert(withoutArea).select("id, qr_code, ticket_tier_id, seat_label");
      data = areaFallback.data;
      error = areaFallback.error;
    }

    if (error) {
      console.error("Failed to issue tickets:", error);
      setIssueFormError(`Failed to issue tickets: ${error.message}`);
      setBusyAction("");
      return;
    }

    for (const ticketRow of data || []) {
      await supabase
        .from("seat_holds")
        .update({ hold_status: "issued", ticket_id: ticketRow.id, updated_at: new Date().toISOString() })
        .eq("event_id", event.id)
        .eq("ticket_tier_id", ticketRow.ticket_tier_id)
        .eq("seat_label", normalizeSeatLabel(ticketRow.seat_label));
    }

    try {
      await notifyBuyerOrderConfirmed({
        toEmail: buyer.email,
        orderId: (data || [])[0]?.id,
        eventTitle: event?.title,
        eventDate: formatEventWhen(event),
        eventVenue: event?.venue,
        eventAddress: event?.address,
        policyText: venuePolicyToPlainText(resolveEventPolicyHtml(event)),
        ticketCount: (data || []).length,
        seatLabels: (data || []).map((row) => row.seat_label),
        tickets: mapIssuedTicketsForEmail(data, payload, tiers, attendeeName, buyer.email),
      });
    } catch (mailError) {
      console.error("Order confirmation email failed:", mailError);
    }

    setIssueTicketOpen(false);
    setIssueAttendeeName("");
    setIssueBuyerEmail("");
    await refreshAfterAction(`${(data || []).length} ticket${(data || []).length === 1 ? "" : "s"} issued to ${attendeeName}.`);
    setBusyAction("");
  };

  const performCompIssue = async () => {
    const selections = getSelectedSeatEntries().filter(
      ({ tierId, seatLabel }) => getOccupancyStatus(tierId, seatLabel) !== "sold"
    );

    if (selections.length === 0) {
      setMessage("Select free or held seats to comp.");
      return;
    }

    setBusyAction("comp");

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const payload = selections.map(({ tierId, seatLabel, seatArea }) => ({
      ticket_tier_id: tierId,
      buyer_id: user?.id || null,
      qr_code:
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      seat_label: seatLabel,
      seat_area: resolveIssuedSeatArea(tierId, seatLabel, seatArea) || null,
      ticket_kind: "comped",
      payment_status: "comped",
      source: "admin",
    }));

    let { data, error } = await supabase.from("tickets").insert(payload).select("id, qr_code, ticket_tier_id, seat_label, seat_area");

    const missingColumnError =
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("ticket_kind") ||
        String(error.message || "").toLowerCase().includes("payment_status") ||
        String(error.message || "").toLowerCase().includes("source"));

    if (missingColumnError) {
      const fallbackPayload = payload.map(({ ticket_tier_id, buyer_id, qr_code, seat_label, seat_area }) => ({
        ticket_tier_id,
        buyer_id,
        qr_code,
        seat_label,
        seat_area,
      }));

      const fallbackResult = await supabase.from("tickets").insert(fallbackPayload).select("id, qr_code, ticket_tier_id, seat_label, seat_area");
      data = fallbackResult.data;
      error = fallbackResult.error;
    }

    if (
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("seat_area"))
    ) {
      const withoutArea = (payload || []).map(({ seat_area, ticket_kind, payment_status, source, ...row }) => ({
        ticket_tier_id: row.ticket_tier_id,
        buyer_id: row.buyer_id,
        qr_code: row.qr_code,
        seat_label: row.seat_label,
      }));
      const areaFallback = await supabase.from("tickets").insert(withoutArea).select("id, qr_code, ticket_tier_id, seat_label");
      data = areaFallback.data;
      error = areaFallback.error;
    }

    if (error) {
      console.error("Failed to issue comped tickets:", error);
      setMessage(`Failed to issue comped tickets: ${error.message}`);
      setBusyAction("");
      return;
    }

    for (const ticketRow of data || []) {
      await supabase
        .from("seat_holds")
        .update({ hold_status: "issued", ticket_id: ticketRow.id, updated_at: new Date().toISOString() })
        .eq("event_id", event.id)
        .eq("ticket_tier_id", ticketRow.ticket_tier_id)
        .eq("seat_label", normalizeSeatLabel(ticketRow.seat_label));
    }

    try {
      await notifyBuyerOrderConfirmed({
        toEmail: user?.email,
        orderId: (data || [])[0]?.id,
        eventTitle: event?.title,
        eventDate: formatEventWhen(event),
        eventVenue: event?.venue,
        eventAddress: event?.address,
        policyText: venuePolicyToPlainText(resolveEventPolicyHtml(event)),
        ticketCount: (data || []).length,
        seatLabels: (data || []).map((row) => row.seat_label),
        tickets: mapIssuedTicketsForEmail(data, payload, tiers, "Guest", user?.email),
      });
    } catch (mailError) {
      console.error("Order confirmation email failed:", mailError);
    }

    await refreshAfterAction(`${(data || []).length} comped ticket${(data || []).length === 1 ? "" : "s"} issued.`);
    setBusyAction("");
  };

  const renderSeatGrid = () => {
    if (!currentTier) return null;

    if (currentSectionLayout?.pages?.length) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold text-gray-500">
              Page {currentPageIndex + 1} of {currentSectionLayout.pages.length}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPageIndexByTier((prev) => ({
                    ...prev,
                    [currentTier.id]: Math.max(0, currentPageIndex - 1),
                  }))
                }
                disabled={currentPageIndex === 0}
                className="w-8 h-8 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-40"
              >
                <ChevronLeft size={16} className="mx-auto" />
              </button>
              <button
                onClick={() =>
                  setPageIndexByTier((prev) => ({
                    ...prev,
                    [currentTier.id]: Math.min(currentSectionLayout.pages.length - 1, currentPageIndex + 1),
                  }))
                }
                disabled={currentPageIndex >= currentSectionLayout.pages.length - 1}
                className="w-8 h-8 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-40"
              >
                <ChevronRight size={16} className="mx-auto" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 py-2">
            <div className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-[11px] font-semibold">
              {currentPage?.label || currentSectionLayout.label}
            </div>
          </div>

          <div className="overflow-x-auto custom-scrollbar pb-2">
            <div
              className="grid justify-center min-w-max"
              style={{
                gridTemplateColumns: `40px repeat(${seatCols.length}, 34px)`,
                gap: "8px",
              }}
            >
              <div />
              {seatCols.map((col, colIndex) => (
                <div key={`col-${colIndex}`} className="text-[10px] font-semibold text-gray-400 text-center">
                  {col}
                </div>
              ))}

              {seatRows.map((rowLabel, rowIndex) => (
                <React.Fragment key={`row-${rowLabel}-${rowIndex}`}>
                  <div className="text-[10px] font-semibold text-gray-500 flex items-center justify-end pr-1">
                    {rowLabel || ""}
                  </div>
                  {seatLayout[rowIndex].map((isSeat, colIndex) => {
                    if (!isSeat) {
                      return <div key={`gap-${rowLabel}-${colIndex}`} className="w-[34px] h-[34px]" />;
                    }

                    const seatNumber =
                      resolveSeatNumber(currentPage, rowIndex, colIndex) ?? colIndex + 1;
                    const seatId = `${rowLabel}${seatNumber}`;
                    const status = getSeatStatus(currentTier.id, seatId);
                    const statusClass =
                      status === "selected"
                        ? "bg-brand-maroon text-white border-brand-maroon"
                        : status === "sold"
                          ? "bg-red-600 text-white border-red-600"
                          : status === "held"
                            ? "bg-amber-400 text-white border-amber-400"
                            : "bg-white text-gray-700 border-gray-200 hover:border-brand-maroon hover:text-brand-maroon";

                    return (
                      <button
                        key={seatId}
                        type="button"
                        onClick={() => toggleSeatSelection(currentTier.id, seatId, currentPageSeatArea)}
                        className={`w-[34px] h-[34px] rounded-md border text-[10px] font-bold transition-all ${statusClass}`}
                        title={`${seatId} - ${status}`}
                      >
                        {seatId}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto custom-scrollbar pb-2">
        <div
          className="grid justify-center min-w-max"
          style={{
            gridTemplateColumns: `40px repeat(${seatCols.length}, 34px)`,
            gap: "8px",
          }}
        >
          <div />
          {seatCols.map((col) => (
            <div key={`col-${col}`} className="text-[10px] font-semibold text-gray-400 text-center">
              {col}
            </div>
          ))}

          {seatRows.map((rowLabel, rowIndex) => {
            const rowSeatCount = rowSeatCounts?.[rowLabel] || seatCols.length;

            return (
              <React.Fragment key={`row-${rowLabel}-${rowIndex}`}>
                <div className="text-[10px] font-semibold text-gray-500 flex items-center justify-end pr-1">
                  {rowLabel}
                </div>
                {seatCols.map((col) => {
                  if (col > rowSeatCount) {
                    return <div key={`gap-${rowLabel}-${col}`} className="w-[34px] h-[34px]" />;
                  }

                  const seatId = `${rowLabel}${col}`;
                  const status = getSeatStatus(currentTier.id, seatId);
                  const statusClass =
                    status === "selected"
                      ? "bg-brand-maroon text-white border-brand-maroon"
                      : status === "sold"
                        ? "bg-red-600 text-white border-red-600"
                        : status === "held"
                          ? "bg-amber-400 text-white border-amber-400"
                          : "bg-white text-gray-700 border-gray-200 hover:border-brand-maroon hover:text-brand-maroon";

                  return (
                    <button
                      key={seatId}
                      type="button"
                      onClick={() => toggleSeatSelection(currentTier.id, seatId, currentPageSeatArea)}
                      className={`w-[34px] h-[34px] rounded-md border text-[10px] font-bold transition-all ${statusClass}`}
                      title={`${seatId} - ${status}`}
                    >
                      {seatId}
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const selectedCount = getSelectedSeatEntries().length;
  const activeSoldCount = Object.values(soldSeatsByTier).reduce((total, seats) => total + (seats?.length || 0), 0);
  const activeHeldCount = Object.values(heldSeatsByTier).reduce((total, seats) => total + (seats?.length || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-[92vw] lg:max-w-6xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-gray-500">Seat Management</p>
            <h3 className="text-lg font-black text-gray-900 tracking-tight break-words">{event?.title || "Event"}</h3>
          </div>
          <button onClick={onClose} className="p-2 min-h-10 min-w-10 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-[10px] font-semibold text-gray-500">Venue</p>
              <p className="mt-1 text-sm font-bold text-gray-900">{event?.venue || "-"}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-[10px] font-semibold text-gray-500">Sold Seats</p>
              <p className="mt-1 text-sm font-bold text-gray-900">{activeSoldCount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-[10px] font-semibold text-gray-500">Held Seats</p>
              <p className="mt-1 text-sm font-bold text-gray-900">{activeHeldCount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-[10px] font-semibold text-gray-500">Selected</p>
              <p className="mt-1 text-sm font-bold text-gray-900">{selectedCount}</p>
            </div>
          </div>

          {message && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {message}
            </div>
          )}

          {!loading && unmappedSoldCount > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {unmappedSoldCount} sold ticket{unmappedSoldCount === 1 ? "" : "s"} do not have seat labels, so they cannot be shown on the seat grid.
            </div>
          )}

          {loading ? (
            <div className="py-16 text-center text-gray-500 font-semibold text-sm">
              Loading seats...
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                {tiers.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTierId(tier.id)}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
                      selectedTierId === tier.id
                        ? "bg-brand-maroon text-white border-brand-maroon"
                        : "bg-white text-gray-600 border-gray-200 hover:border-brand-maroon hover:text-brand-maroon"
                    }`}
                  >
                    {tier.section || tier.name || "Tier"}
                  </button>
                ))}
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h4 className="text-sm font-black text-gray-900 tracking-tight break-words">{currentTier?.name || "Seat Tier"}</h4>
                    <p className="text-[11px] text-gray-500 font-medium">
                      {currentTier?.section || event?.venue || "All seats"}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={requestHold}
                      disabled={busyAction === "hold"}
                      className="px-4 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
                    >
                      <Lock size={14} /> {busyAction === "hold" ? "Holding..." : "Hold Selected"}
                    </button>
                    <button
                      type="button"
                      onClick={performRelease}
                      disabled={busyAction === "release"}
                      className="px-4 py-2 rounded-lg bg-gray-700 text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
                    >
                      <RefreshCcw size={14} /> {busyAction === "release" ? "Releasing..." : "Release Held"}
                    </button>
                    <button
                      type="button"
                      onClick={requestIssueTicket}
                      disabled={busyAction === "issue"}
                      className="px-4 py-2 rounded-lg bg-blue-800 text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
                    >
                      <Ticket size={14} /> {busyAction === "issue" ? "Issuing..." : "Issue Ticket"}
                    </button>
                    <button
                      type="button"
                      onClick={performCompIssue}
                      disabled={busyAction === "comp"}
                      className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
                    >
                      <Check size={14} /> {busyAction === "comp" ? "Issuing..." : "Issue Comp"}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold text-gray-500 mb-4">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-white border border-gray-200" />Available</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-400" />Held</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-600" />Sold</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-brand-maroon" />Selected</span>
                </div>

                {renderSeatGrid()}
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-[10px] font-semibold text-gray-500">Selected Seats</p>
                  <button type="button" onClick={clearSelection} className="text-xs font-bold text-brand-maroon hover:underline">
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getSelectedSeatEntries().length > 0 ? (
                    getSelectedSeatEntries().map((entry) => {
                      const sectionLabel = resolveIssuedSeatArea(entry.tierId, entry.seatLabel, entry.seatArea);
                      return (
                      <span key={`${entry.tierId}-${entry.seatArea}-${entry.seatLabel}`} className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-[11px] font-semibold text-gray-700">
                        {sectionLabel ? `${sectionLabel} ${entry.seatLabel}` : entry.seatLabel}
                      </span>
                      );
                    })
                  ) : (
                    <span className="text-sm text-gray-500">No seats selected.</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmHoldOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
            <h4 className="text-base font-black text-gray-900 tracking-tight">Are you sure to hold these seats?</h4>
            <p className="mt-3 text-sm text-gray-600">
              {getHoldableSelections(getSelectedSeatEntries(), getOccupancyStatus)
                .map((entry) => {
                  const sectionLabel = resolveIssuedSeatArea(entry.tierId, entry.seatLabel, entry.seatArea);
                  return sectionLabel ? `${sectionLabel} ${entry.seatLabel}` : entry.seatLabel;
                })
                .join(", ") || "No seats selected."}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmHoldOpen(false)}
                disabled={busyAction === "hold"}
                className="px-4 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performHold}
                disabled={busyAction === "hold"}
                className="px-4 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold disabled:opacity-60"
              >
                {busyAction === "hold" ? "Holding..." : "Hold seats"}
              </button>
            </div>
          </div>
        </div>
      )}

      {issueTicketOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
            <h4 className="text-base font-black text-gray-900 tracking-tight">Issue Ticket</h4>
            <p className="mt-2 text-sm text-gray-600">
              Seats:{" "}
              {getSelectedSeatEntries()
                .filter(({ tierId, seatLabel }) => (heldSeatsByTier[tierId] || []).includes(seatLabel))
                .map((entry) => {
                  const sectionLabel = resolveIssuedSeatArea(entry.tierId, entry.seatLabel, entry.seatArea);
                  return sectionLabel ? `${sectionLabel} ${entry.seatLabel}` : entry.seatLabel;
                })
                .join(", ")}
            </p>
            <label className="mt-4 block">
              <span className="text-[10px] font-semibold text-gray-500">Customer / Attendee Name</span>
              <input
                type="text"
                value={issueAttendeeName}
                onChange={(event) => setIssueAttendeeName(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-maroon/20"
                placeholder="Full name on the ticket"
              />
            </label>
            <label className="mt-3 block">
              <span className="text-[10px] font-semibold text-gray-500">Buyer Echo Jham Email</span>
              <input
                type="email"
                value={issueBuyerEmail}
                onChange={(event) => setIssueBuyerEmail(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-maroon/20"
                placeholder="buyer@email.com"
              />
            </label>
            {issueFormError && <p className="mt-3 text-sm text-red-600">{issueFormError}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIssueTicketOpen(false);
                  setIssueFormError("");
                }}
                disabled={busyAction === "issue"}
                className="px-4 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performIssueTicket}
                disabled={busyAction === "issue"}
                className="px-4 py-2 rounded-lg bg-blue-800 text-white text-xs font-semibold disabled:opacity-60"
              >
                {busyAction === "issue" ? "Issuing..." : "Issue tickets"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatManagementModal;